import React, { useState, useRef } from "react";
import {
  Box,
  Popover,
  PopoverTrigger,
  PopoverContent,
  PopoverBody,
  HStack,
  VStack,
  Icon,
  Text,
  Input,
  Button,
} from "@chakra-ui/react";
import { FaPalette, FaPlus } from "react-icons/fa";
import { Editor } from "@tiptap/react";
import { useEditorState } from "@tiptap/react";
import { ColorButton } from "./ColorButton";

interface ColorSelectorProps {
  editor: Editor;
}

const COLORS = [
  { name: "Dark Red", value: "#8B0000" },
  { name: "Red", value: "#FF0000" },
  { name: "Orange", value: "#FFA500" },
  { name: "Yellow", value: "#FFFF00" },
  { name: "Light Green", value: "#90EE90" },
  { name: "Teal", value: "#008080" },
  { name: "Light Blue", value: "#87CEEB" },
  { name: "Blue", value: "#0000FF" },
  { name: "Purple", value: "#800080" },
  { name: "Pink", value: "#FFC0CB" },
  { name: "Black", value: "#000000" },
  { name: "Dark Gray", value: "#404040" },
  { name: "Gray", value: "#808080" },
  { name: "Light Gray", value: "#D3D3D3" },
];

export function ColorSelector({ editor }: ColorSelectorProps) {
  const [isOpen, setIsOpen] = useState(false);
  const colorInputRef = useRef<HTMLInputElement>(null);

  // Use editor state to make the component reactive to selection changes
  const editorState = useEditorState({
    editor,
    selector: (ctx) => {
      return {
        currentColor: ctx.editor.getAttributes("textStyle")?.color || "#000000",
        isTextStyleActive: ctx.editor.isActive("textStyle"),
      };
    },
  });

  const handleColorSelect = (color: string) => {
    editor.chain().focus().setColor(color).run();
    setIsOpen(false);
  };

  const handleCustomColor = () => {
    colorInputRef.current?.click();
  };

  const handleCustomColorChange = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    const color = event.target.value;
    if (color) {
      handleColorSelect(color);
    }
  };

  return (
    <Popover
      isOpen={isOpen}
      onClose={() => setIsOpen(false)}
      placement="bottom"
    >
      <PopoverTrigger>
        <Box
          as="button"
          type="button"
          w={5}
          h={5}
          borderRadius="full"
          bg={editorState.currentColor}
          display="flex"
          alignItems="center"
          justifyContent="center"
          cursor="pointer"
          _hover={{
            borderColor: "gray.400",
            transform: "scale(1.05)",
          }}
          _active={{
            transform: "scale(0.95)",
          }}
          transition="all 0.2s"
          onClick={() => setIsOpen(!isOpen)}
          title={`Current text color: ${editorState.currentColor}`}
        ></Box>
      </PopoverTrigger>
      <PopoverContent w="200px" p={1}>
        <PopoverBody>
          <VStack spacing={3} align="stretch">
            <Text
              fontWeight="bold"
              fontSize="sm"
              color="gray.700"
              mb={"0 !important"}
            >
              Text Color
            </Text>

            {/* Hidden native color input */}
            <Input
              ref={colorInputRef}
              type="color"
              position="absolute"
              opacity={0}
              pointerEvents="none"
              onChange={handleCustomColorChange}
            />

            {/* Color grid - 3 rows of 5 colors each */}
            <VStack spacing={2}>
              {/* Row 1 */}
              <HStack spacing={2} justify="space-between" w="full">
                {COLORS.slice(0, 5).map((color) => (
                  <ColorButton
                    key={color.value}
                    color={color.value}
                    name={color.name}
                    isSelected={editorState.currentColor === color.value}
                    onClick={() => handleColorSelect(color.value)}
                    size="sm"
                  />
                ))}
              </HStack>

              {/* Row 2 */}
              <HStack spacing={2} justify="space-between" w="full">
                {COLORS.slice(5, 10).map((color) => (
                  <ColorButton
                    key={color.value}
                    color={color.value}
                    name={color.name}
                    isSelected={editorState.currentColor === color.value}
                    onClick={() => handleColorSelect(color.value)}
                    size="sm"
                  />
                ))}
              </HStack>

              {/* Row 3 */}
              <HStack spacing={2} justify="space-between" w="full">
                {COLORS.slice(10, 14).map((color) => (
                  <ColorButton
                    key={color.value}
                    color={color.value}
                    name={color.name}
                    isSelected={editorState.currentColor === color.value}
                    onClick={() => handleColorSelect(color.value)}
                    size="sm"
                  />
                ))}

                {/* Plus button for custom color */}
                <ColorButton
                  color="gray.200"
                  name="Custom Color"
                  onClick={handleCustomColor}
                  showPlusIcon={true}
                  size="sm"
                />
              </HStack>
            </VStack>

            {/* Remove color button */}
            <Button
              type="button"
              w="full"
              variant="ghost"
              fontSize="xs"
              size="sm"
              onClick={() => {
                editor.chain().focus().unsetColor().run();
                setIsOpen(false);
              }}
            >
              Reset Color
            </Button>
          </VStack>
        </PopoverBody>
      </PopoverContent>
    </Popover>
  );
}
